// src/components/service-billing/RejectInvoiceDialog.tsx
import { Button, Dialog, Portal, Text, Field, Box, Textarea } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

const COPY = {
  title: 'Rechnung ablehnen',
  body: 'Bitte dokumentieren Sie den Grund für die Ablehnung der Rechnung.',
  reasonLabel: 'Ablehnungsgrund',
  reasonPlaceholder: '',
  reasonRequired: 'Bitte geben Sie einen Ablehnungsgrund ein.',
  counter: (n: number, max: number) => `${n} von ${max} Zeichen.`,
  cancel: 'Abbrechen',
  confirm: 'Rechnung ablehnen',
}

interface RejectInvoiceDialogProps {
  isOpen: boolean
  onClose: () => void
  loading?: boolean
  error?: string | null
  onConfirm: (rejectionReason: string) => Promise<void> | void
}

const MAX_LEN = 500

export function RejectInvoiceDialog({
  isOpen,
  onClose,
  onConfirm,
  loading,
  error,
}: RejectInvoiceDialogProps) {
  const t = COPY

  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setReason('')
    setTouched(false)
  }, [isOpen])

  const trimmed = reason.trim()
  const reasonError = touched && !trimmed ? t.reasonRequired : null
  const canSubmit = !loading && !!trimmed

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={details => {
        if (!details.open) onClose()
      }}
      size="md"
      closeOnInteractOutside={false}
      closeOnEscape={false}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content mx={{ base: 4, md: 'auto' }} rounded="l3" shadow="lg">
            <Dialog.Header>
              <Dialog.Title fontSize="lg" fontWeight="600">
                {t.title}
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body color="fg.muted" fontSize="sm">
              <Text mb="4">{t.body}</Text>

              <Box mt="4">
                <Field.Root invalid={!!reasonError}>
                  <Field.Label fontWeight="semibold">
                    {t.reasonLabel}
                    <Text as="span" color="fg.error">
                      {' '}
                      *
                    </Text>
                  </Field.Label>

                  <Textarea
                    value={reason}
                    onChange={e => setReason(e.target.value.slice(0, MAX_LEN))}
                    onBlur={() => setTouched(true)}
                    placeholder={t.reasonPlaceholder}
                    maxLength={MAX_LEN}
                    resize="vertical"
                    minH="48px"
                  />

                  <Field.HelperText fontSize="xs" color="fg.muted" mt="1">
                    {t.counter(reason.length, MAX_LEN)}
                  </Field.HelperText>

                  {reasonError ? (
                    <Field.ErrorText fontSize="xs">{reasonError}</Field.ErrorText>
                  ) : null}
                </Field.Root>
              </Box>

              {error ? (
                <Text mt="3" color="red.500" fontSize="sm">
                  {error}
                </Text>
              ) : null}
            </Dialog.Body>

            <Dialog.Footer gap={3}>
              <Button variant="outline" size="md" onClick={onClose} disabled={!!loading}>
                {t.cancel}
              </Button>

              <Button
                size="md"
                colorPalette="red"
                loading={!!loading}
                disabled={!canSubmit}
                onClick={() => onConfirm(trimmed)}
              >
                {t.confirm}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
